import { useMemo, useState } from 'react';
import { addHours, format } from 'date-fns';
import {
  ArrowRight,
  CalendarClock,
  CalendarPlus,
  ChevronLeft,
  Sparkles,
  Users,
  XCircle,
} from 'lucide-react';
import clsx from 'clsx';
import { aud } from '@/utils/format';
import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { prefillOutcomeFromIntent } from '@/domain/sessionFlow';
import { generateHealingSlots } from '@/utils/rebookWindow';
import type {
  Artist,
  Booking,
  CustomerRecord,
  Deposit,
  RebookAttribution,
  RebookOutcome,
} from '@/domain/types';

type OutcomeKind = RebookOutcome['kind'];

/**
 * L3 (17 Jul): on a decline-path checkout the manager records how far the piece
 * is - finished, or partway with an approximate number of sessions still to go.
 * `checkOutBooking` turns partway into `estimatedTotalSessions` (a living number)
 * and finished into the existing mark-piece-complete path.
 */
export type PieceStateCapture = { finished: boolean; sessionsLeft?: number };

interface Props {
  booking: Booking;
  customer: CustomerRecord;
  artist: Artist;
  deposit?: Deposit;
  sessionPrice: number;
  onBack: () => void;
  onContinue: (capture: {
    outcome: RebookOutcome;
    attribution: RebookAttribution;
    selectedSlotIso?: string;
    pieceState?: PieceStateCapture;
  }) => void;
  /** Opens the calendar to pick a custom rebook date (Outcome A). */
  onPickCustomDate?: () => void;
  /** Seed the outcome on return from the calendar (forces the in-chair rebook). */
  initialOutcome?: OutcomeKind;
  /** A slot chosen on the calendar, shown selected in the picker. */
  customSlot?: { iso: string };
}

export default function RebookOutcomePanel({
  booking,
  customer,
  artist,
  deposit,
  sessionPrice,
  onBack,
  onContinue,
  onPickCustomDate,
  initialOutcome,
  customSlot,
}: Props) {
  // Outcome seeds from an explicit initialOutcome (calendar return) first, else
  // from the artist's in-chair read. The manager can always override. The dead
  // C-kind (defer to sales) maps to "will rebook later", which now carries the
  // deferral (L3).
  const [outcome, setOutcome] = useState<OutcomeKind | null>(() => {
    const seed = initialOutcome ?? prefillOutcomeFromIntent(booking.artistRebookIntent);
    return seed === 'rebookDeferredToSales' ? 'willRebookLater' : seed;
  });

  const [selectedSlot, setSelectedSlot] = useState<string | null>(customSlot?.iso ?? null);
  // Mandatory free-text reasons (L3): no chips - the manager must type why.
  const [declineReasonText, setDeclineReasonText] = useState('');
  const [laterReasonText, setLaterReasonText] = useState('');
  // Piece-state capture on the decline paths (only when a piece is linked).
  const [pieceStage, setPieceStage] = useState<'partway' | 'finished' | null>(null);
  const [sessionsLeftInput, setSessionsLeftInput] = useState('');
  // Default to crediting the artist. The manager picking otherwise is
  // intentional - they had to step in. Friendly default nudges fair credit.
  const [facilitatedBy, setFacilitatedBy] = useState<RebookAttribution['facilitatedBy']>('artist');
  const [otherStaffId, setOtherStaffId] = useState<string>('');

  const studios = useBrainStore((s) => s.studios);
  const artists = useBrainStore((s) => s.artists);
  const agents = useBrainStore((s) => s.agents);
  const clockIso = useSimulationStore((s) => s.clockIso);

  const studio = studios[booking.studioId];
  const managerOnDuty = studio ? agents[studio.managerId] : undefined;
  // Suggested slots in three healing bands (2/3/4 weeks), never earlier than 2
  // weeks after the sitting.
  const bands = useMemo(
    () => generateHealingSlots(booking.start, clockIso),
    [booking.start, clockIso],
  );

  // List of "other team members at this studio today" for the picker.
  const otherStaffOptions = useMemo(() => {
    const studioArtists = Object.values(artists).filter((a) => a.studioId === booking.studioId);
    const studioAgents = Object.values(agents).filter(
      (a) => a.assignedStudios.includes(booking.studioId) || a.primaryStudio === booking.studioId,
    );
    return [
      ...studioArtists.map((a) => ({
        id: a.id,
        label: `${a.displayName || a.firstName} · artist`,
      })),
      ...studioAgents.map((a) => ({ id: a.id, label: `${a.firstName} · ${a.role}` })),
    ].filter((o) => o.id !== artist.id && o.id !== studio?.managerId);
  }, [artists, agents, booking.studioId, artist.id, studio?.managerId]);

  const depositAmount = deposit?.amountAud ?? 0;
  const amountForOutcomeB = Math.max(0, sessionPrice - depositAmount);

  // Attribution applies to the two rebook wins (lock-now + will-rebook-later);
  // decline credits the artist.
  const effectiveFacilitatedBy: RebookAttribution['facilitatedBy'] =
    outcome === 'noRebookDepositApplied' ? 'artist' : facilitatedBy;
  const showsAttribution = outcome === 'rebookedInChair' || outcome === 'willRebookLater';

  // L3 piece-state gating: on a decline path with a linked piece the manager must
  // record finished, or partway with at least one session still to go.
  const hasPiece = !!booking.pieceId;
  const isDeclinePath = outcome === 'willRebookLater' || outcome === 'noRebookDepositApplied';
  const sessionsLeftNum = Number(sessionsLeftInput);
  const pieceStateValid =
    !hasPiece ||
    !isDeclinePath ||
    pieceStage === 'finished' ||
    (pieceStage === 'partway' && Number.isFinite(sessionsLeftNum) && sessionsLeftNum >= 1);

  const continueEnabled =
    !!outcome &&
    (outcome !== 'rebookedInChair' || !!selectedSlot) &&
    (outcome !== 'willRebookLater' || laterReasonText.trim().length > 0) &&
    (outcome !== 'noRebookDepositApplied' || declineReasonText.trim().length > 0) &&
    pieceStateValid &&
    (!showsAttribution || effectiveFacilitatedBy !== 'otherStaff' || !!otherStaffId);

  function build(): {
    outcome: RebookOutcome;
    attribution: RebookAttribution;
    selectedSlotIso?: string;
    pieceState?: PieceStateCapture;
  } | null {
    if (!outcome || !effectiveFacilitatedBy) return null;
    const capturedAt = new Date().toISOString();

    let facilitatedByStaffId: string;
    if (effectiveFacilitatedBy === 'artist') facilitatedByStaffId = artist.id;
    else if (effectiveFacilitatedBy === 'floorManager') facilitatedByStaffId = studio?.managerId ?? 'unknown';
    else if (effectiveFacilitatedBy === 'salesTeam') facilitatedByStaffId = 'sales-team';
    else facilitatedByStaffId = otherStaffId;

    const attribution: RebookAttribution = {
      facilitatedBy: effectiveFacilitatedBy,
      facilitatedByStaffId,
      capturedAt,
    };
    const pieceState: PieceStateCapture | undefined =
      hasPiece && isDeclinePath
        ? {
            finished: pieceStage === 'finished',
            sessionsLeft: pieceStage === 'partway' ? sessionsLeftNum : undefined,
          }
        : undefined;

    if (outcome === 'rebookedInChair' && selectedSlot) {
      return {
        outcome: {
          kind: 'rebookedInChair',
          newBookingId: `bk-rebook-${booking.id}-${Date.now()}`,
          depositRolledForward: depositAmount > 0,
        },
        attribution,
        selectedSlotIso: selectedSlot,
      };
    }
    if (outcome === 'willRebookLater') {
      return {
        outcome: {
          kind: 'willRebookLater',
          depositRolledForward: depositAmount > 0,
          note: laterReasonText.trim(),
        },
        attribution,
        pieceState,
      };
    }
    if (outcome === 'noRebookDepositApplied') {
      return {
        outcome: {
          kind: 'noRebookDepositApplied',
          reason: declineReasonText.trim(),
        },
        attribution,
        pieceState,
      };
    }
    return null;
  }

  function handleContinue() {
    const built = build();
    if (!built) return;
    onContinue(built);
  }

  return (
    <div className="panel p-6 space-y-5" data-testid="rebook-outcome-panel">
      <header>
        <p className="label-small-caps text-gold-bright">Rebook outcome</p>
        <p className="font-serif text-cream-soft text-sm mt-1">
          {customer.firstName}&apos;s session with {artist.firstName} is wrapped. Capture what happens
          next before payment - this drives the artist + manager leaderboard.
        </p>
      </header>

      <section className="space-y-3">
        <p className="label-small-caps">What happens next?</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="rebook-outcome-buttons">
          <OutcomeButton
            active={outcome === 'rebookedInChair'}
            onClick={() => setOutcome('rebookedInChair')}
            Icon={CalendarPlus}
            tone="mint"
            title="Lock next session now"
            subtitle={`Roll ${aud(depositAmount)} deposit forward · pick a slot · pay ${aud(sessionPrice)} today`}
            testid="rebook-outcome-A"
          />
          <OutcomeButton
            active={outcome === 'willRebookLater'}
            onClick={() => setOutcome('willRebookLater')}
            Icon={CalendarClock}
            tone="mint"
            title="Will rebook - book later"
            subtitle={`Roll ${aud(depositAmount)} deposit forward to hold · no slot yet · pay ${aud(sessionPrice)} today`}
            testid="rebook-outcome-later"
          />
          <OutcomeButton
            active={outcome === 'noRebookDepositApplied'}
            onClick={() => setOutcome('noRebookDepositApplied')}
            Icon={XCircle}
            tone="blood"
            title="No future booking"
            subtitle={`Apply ${aud(depositAmount)} deposit · charge ${aud(amountForOutcomeB)} today`}
            testid="rebook-outcome-B"
          />
        </div>
      </section>

      {/* Outcome A: healing-banded slot picker + custom date */}
      {outcome === 'rebookedInChair' ? (
        <section className="space-y-3" data-testid="rebook-slot-picker">
          <p className="label-small-caps">Pick next slot with {artist.firstName}</p>
          <div
            className="rounded-md border border-mint/40 bg-mint/5 px-3 py-2 flex items-start gap-2"
            data-testid="rebook-healing-note"
          >
            <Sparkles size={13} className="text-mint shrink-0 mt-0.5" />
            <p className="font-serif text-[12px] text-cream-soft">
              Tattoos need ~2 weeks to heal before the next sitting, so the earliest suggestions start
              2 weeks out.
            </p>
          </div>

          {customSlot ? (
            <button
              type="button"
              data-testid="rebook-custom-slot"
              onClick={() => setSelectedSlot(customSlot.iso)}
              className={clsx(
                'w-full rounded-md border px-3 py-2 text-left text-sm transition flex items-center gap-2',
                selectedSlot === customSlot.iso
                  ? 'border-mint bg-mint/10 text-mint'
                  : 'border-gold-dim/30 text-cream-soft hover:border-gold-bright/60',
              )}
            >
              <span className="pill-mint text-[9px]">Custom</span>
              <span className="font-inter">
                {format(new Date(customSlot.iso), 'EEE d MMM')} ·{' '}
                {format(new Date(customSlot.iso), 'h:mma').toLowerCase()}
              </span>
            </button>
          ) : null}

          {bands.map((band) => (
            <div key={band.key} data-testid={`rebook-band-${band.key}`} className="space-y-1.5">
              <p className="font-inter text-[10px] uppercase tracking-widest text-cream-faint">
                {band.weeks} weeks{band.key === '2wk' ? ' · earliest - healed' : ''}
              </p>
              <div className="grid grid-cols-2 gap-2">
                {band.slots.map((iso) => {
                  const active = selectedSlot === iso;
                  return (
                    <button
                      key={iso}
                      type="button"
                      onClick={() => setSelectedSlot(iso)}
                      data-testid={`rebook-slot-${iso}`}
                      data-slot-iso={iso}
                      className={clsx(
                        'rounded-md border px-3 py-2 text-left text-sm transition',
                        active
                          ? 'border-mint bg-mint/10 text-mint'
                          : 'border-gold-dim/30 text-cream-soft hover:border-gold-bright/60',
                      )}
                    >
                      <span className="block font-inter">{format(new Date(iso), 'EEE d MMM')}</span>
                      <span className="block font-inter text-[10px] uppercase tracking-widest text-cream-faint mt-1">
                        {format(new Date(iso), 'h:mma').toLowerCase()} ·{' '}
                        {format(addHours(new Date(iso), 4), 'h:mma').toLowerCase()}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          {onPickCustomDate ? (
            <div className="pt-3 border-t border-dashed border-gold-dim/40">
              <button
                type="button"
                onClick={onPickCustomDate}
                data-testid="rebook-pick-custom"
                className="btn-ghost text-sm"
              >
                <CalendarPlus size={14} /> Pick a custom date on the calendar
              </button>
              <p className="font-serif text-[12px] text-cream-soft mt-1.5">
                Opens {artist.firstName}&apos;s calendar. Dates under 2 weeks ask you to confirm the
                healing window. You will come back here with the date filled in.
              </p>
            </div>
          ) : null}
        </section>
      ) : null}

      {/* Will rebook later: deposit rolls to hold, mandatory free-text reason
          (the deferral note for the sales team) + piece-state capture. */}
      {outcome === 'willRebookLater' ? (
        <section className="space-y-2" data-testid="rebook-later-note">
          <p className="label-small-caps">
            Why not now? <span aria-hidden="true">*</span>
          </p>
          <textarea
            value={laterReasonText}
            onChange={(e) => setLaterReasonText(e.target.value)}
            placeholder="Type why they're not booking today - this goes to the sales team to follow up. Required."
            rows={2}
            className="w-full bg-ink-deep border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream font-serif"
            data-testid="rebook-later-reason-input"
          />
          <p className="font-serif text-[12px] text-cream-soft">
            {aud(depositAmount)} deposit rolls forward as a held pot - no slot booked yet. Sales
            follows up.
          </p>
          <PieceStateCapture
            hasPiece={hasPiece}
            stage={pieceStage}
            onStage={setPieceStage}
            sessionsLeft={sessionsLeftInput}
            onSessionsLeft={setSessionsLeftInput}
          />
        </section>
      ) : null}

      {/* Outcome B: mandatory free-text reason + piece-state capture. */}
      {outcome === 'noRebookDepositApplied' ? (
        <section className="space-y-2" data-testid="rebook-decline-reason">
          <p className="label-small-caps">
            Why aren&apos;t they rebooking? <span aria-hidden="true">*</span>
          </p>
          <textarea
            value={declineReasonText}
            onChange={(e) => setDeclineReasonText(e.target.value)}
            placeholder="Type why they're not rebooking. Required."
            rows={2}
            className="w-full bg-ink-deep border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream font-serif"
            data-testid="rebook-decline-reason-input"
          />
          <PieceStateCapture
            hasPiece={hasPiece}
            stage={pieceStage}
            onStage={setPieceStage}
            sessionsLeft={sessionsLeftInput}
            onSessionsLeft={setSessionsLeftInput}
          />
        </section>
      ) : null}

      {/* Attribution selector - the two rebook wins (lock-now + will-rebook-later).
          The artist is the default so the manager has to actively switch off it
          when they stepped in. Keeps credit fair without feeling competitive. */}
      {showsAttribution ? (
        <section className="space-y-2" data-testid="rebook-attribution">
          <p className="label-small-caps flex items-center gap-1.5">
            <Users size={11} /> Credit this rebook to...
          </p>
          <p className="font-serif text-[12px] text-cream-soft">
            Default is to credit the artist. Only switch if you stepped in cold to close it.
          </p>
          <div className="flex flex-col gap-1.5">
            <AttrRadio
              label={`${artist.firstName} closed it`}
              value="artist"
              active={effectiveFacilitatedBy === 'artist'}
              disabled={false}
              onClick={() => setFacilitatedBy('artist')}
              testid="rebook-attr-artist"
              tone="mint"
            />
            <AttrRadio
              label={`${managerOnDuty?.firstName ?? 'Manager'} stepped in`}
              value="floorManager"
              active={effectiveFacilitatedBy === 'floorManager'}
              disabled={false}
              onClick={() => setFacilitatedBy('floorManager')}
              testid="rebook-attr-manager"
              tone="gold"
            />
            <AttrRadio
              label="Someone else helped"
              value="otherStaff"
              active={effectiveFacilitatedBy === 'otherStaff'}
              disabled={false}
              onClick={() => setFacilitatedBy('otherStaff')}
              testid="rebook-attr-other"
              tone="cream"
            />
            {effectiveFacilitatedBy === 'otherStaff' ? (
              <select
                value={otherStaffId}
                onChange={(e) => setOtherStaffId(e.target.value)}
                className="ml-6 mt-1 bg-ink-deep border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream"
                data-testid="rebook-attr-other-picker"
              >
                <option value="">Pick someone...</option>
                {otherStaffOptions.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.label}
                  </option>
                ))}
              </select>
            ) : null}
          </div>
        </section>
      ) : null}

      <footer className="flex justify-between pt-2 border-t border-gold-dim/15">
        <button
          type="button"
          onClick={onBack}
          className="btn-quiet text-sm"
          data-testid="rebook-back"
        >
          <ChevronLeft size={12} /> Back
        </button>
        <button
          type="button"
          onClick={handleContinue}
          disabled={!continueEnabled}
          className="btn-primary text-sm disabled:opacity-40"
          data-testid="rebook-continue"
        >
          Continue <ArrowRight size={14} />
        </button>
      </footer>
    </div>
  );
}

/**
 * L3 piece-state capture, shown on the decline paths when a piece is linked. Two
 * options - partway or finished; partway reveals an "approximate sessions left"
 * number that becomes the piece's living `estimatedTotalSessions` at checkout.
 */
function PieceStateCapture({
  hasPiece,
  stage,
  onStage,
  sessionsLeft,
  onSessionsLeft,
}: {
  hasPiece: boolean;
  stage: 'partway' | 'finished' | null;
  onStage: (s: 'partway' | 'finished') => void;
  sessionsLeft: string;
  onSessionsLeft: (v: string) => void;
}) {
  if (!hasPiece) return null;
  return (
    <div className="mt-3 pt-3 border-t border-dashed border-gold-dim/30" data-testid="rebook-piece-state">
      <p className="label-small-caps mb-1.5">
        Where&apos;s the piece up to? <span aria-hidden="true">*</span>
      </p>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => onStage('partway')}
          data-testid="rebook-piece-partway"
          aria-pressed={stage === 'partway'}
          className={clsx(
            'flex-1 rounded-md border px-3 py-2 text-sm transition',
            stage === 'partway'
              ? 'border-gold-bright bg-gold/10 text-gold-bright'
              : 'border-gold-dim/30 text-cream-soft hover:border-gold-bright/50',
          )}
        >
          Partway finished
        </button>
        <button
          type="button"
          onClick={() => onStage('finished')}
          data-testid="rebook-piece-finished"
          aria-pressed={stage === 'finished'}
          className={clsx(
            'flex-1 rounded-md border px-3 py-2 text-sm transition',
            stage === 'finished'
              ? 'border-mint bg-mint/10 text-mint'
              : 'border-gold-dim/30 text-cream-soft hover:border-gold-bright/50',
          )}
        >
          Finished
        </button>
      </div>
      {stage === 'partway' ? (
        <div className="mt-2">
          <label htmlFor="rebook-sessions-left" className="label-small-caps text-cream-faint">
            Approximate sessions left
          </label>
          <input
            id="rebook-sessions-left"
            type="number"
            min={1}
            step={1}
            inputMode="numeric"
            value={sessionsLeft}
            onChange={(e) => onSessionsLeft(e.target.value)}
            placeholder="e.g. 3"
            className="mt-1 w-full bg-ink-deep border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream"
            data-testid="rebook-sessions-left"
          />
          <p className="font-serif text-[12px] text-cream-soft mt-1">
            Updates the piece&apos;s total sessions estimate - it keeps refreshing each visit.
          </p>
        </div>
      ) : null}
    </div>
  );
}

function OutcomeButton({
  active,
  onClick,
  Icon,
  tone,
  title,
  subtitle,
  testid,
}: {
  active: boolean;
  onClick: () => void;
  Icon: typeof CalendarPlus;
  tone: 'mint' | 'blood' | 'gold';
  title: string;
  subtitle: string;
  testid: string;
}) {
  const toneActive =
    tone === 'mint'
      ? 'border-mint bg-mint/10 text-mint'
      : tone === 'blood'
        ? 'border-blood bg-blood/10 text-blood'
        : 'border-gold-bright bg-gold/10 text-gold-bright';
  const toneIdle =
    tone === 'mint'
      ? 'border-mint/30 text-cream-soft hover:border-mint/70'
      : tone === 'blood'
        ? 'border-blood/30 text-cream-soft hover:border-blood/70'
        : 'border-gold-dim/30 text-cream-soft hover:border-gold-bright/60';

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={clsx(
        'rounded-lg border p-4 text-left transition flex flex-col gap-2',
        active ? toneActive : toneIdle,
      )}
    >
      <span className="flex items-center gap-2">
        <Icon size={14} />
        <span className="font-inter text-sm">{title}</span>
      </span>
      <span className="font-serif text-xs leading-relaxed">{subtitle}</span>
    </button>
  );
}

function AttrRadio({
  label,
  active,
  disabled,
  onClick,
  testid,
  tone = 'gold',
}: {
  label: string;
  value: string;
  active: boolean;
  disabled: boolean;
  onClick: () => void;
  testid: string;
  tone?: 'mint' | 'gold' | 'cream';
}) {
  const activeClasses =
    tone === 'mint'
      ? 'border-mint bg-mint/10 text-mint'
      : tone === 'cream'
        ? 'border-cream-soft/60 bg-cream-soft/10 text-cream'
        : 'border-gold-bright bg-gold/10 text-gold-bright';
  const dotClasses =
    tone === 'mint'
      ? 'bg-mint border-mint'
      : tone === 'cream'
        ? 'bg-cream-soft border-cream-soft'
        : 'bg-gold-bright border-gold-bright';
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      data-testid={testid}
      className={clsx(
        'flex items-center gap-2 px-3 py-1.5 rounded-md border text-sm transition text-left',
        active
          ? activeClasses
          : 'border-gold-dim/30 text-cream-soft hover:border-gold-bright/50',
        disabled ? 'opacity-40 cursor-not-allowed' : '',
      )}
    >
      <span
        className={clsx(
          'inline-block h-2.5 w-2.5 rounded-full border',
          active ? dotClasses : 'border-gold-dim/60',
        )}
      />
      <span>{label}</span>
      <span className="hidden">
        <Sparkles size={1} />
      </span>
    </button>
  );
}
